import { createContext, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import { F_KEY } from "constants/hotKeys.enums";
import { RootState } from "store/rootReducer";

import { useFieldTitleWithUnit } from "hooks/useFieldTitleWithUnit";

import { IGroupBy } from "models";
import { EntityKind } from "models/entityKind";

import RecentFieldsButton from "components/base/RecentFieldsButton";
import GroupBy from "components/groupBy";

import { useRecentFocusItems } from "../hooks";
import useTileColumns from "../hooks/useTileColumns";

export const PresetDropdownContext = createContext<boolean>(false);

const PresetDropdown = ({ onGroupByChange, onRecentFocusItemClick, entityKind }) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const { recentFocusItems, isPresetFieldSelected } = useRecentFocusItems(entityKind);

  const { isLoading } = useTileColumns(entityKind);

  const groupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const facilityGroupBy = useSelector(
    (state: RootState) => state.groupBy.globalFacilityFocus
  );
  const [groupByState, setGroupByState] = useState<IGroupBy>(
    entityKind === EntityKind.Well ? groupBy : facilityGroupBy
  );
  useEffect(() => {
    const gb = entityKind === EntityKind.Well ? groupBy : facilityGroupBy;
    setGroupByState(gb);
  }, [groupBy, facilityGroupBy, entityKind]);

  const correctedTitle = useFieldTitleWithUnit(
    groupByState?.title,
    groupByState?.property
  );

  useHotkeys(
    F_KEY,
    () => {
      // Check if this component's button is actually visible/clickable
      if (!buttonRef.current || !buttonRef.current.offsetParent) {
        return;
      }

      buttonRef.current.click();
    },
    {
      preventDefault: true,
      enableOnContentEditable: false,
      enabled: true
    },
    []
  );

  return (
    <GroupBy
      value={groupByState}
      placement="rightTop"
      onChange={onGroupByChange}
      ownsFocus
      allowsShapefileFilter
      entityKind={entityKind}>
      <RecentFieldsButton
        buttonRef={buttonRef}
        text={isLoading ? "" : isPresetFieldSelected ? correctedTitle : "More Fields"}
        isSelected={isPresetFieldSelected}
        recentItems={recentFocusItems}
        onRecentItemClick={onRecentFocusItemClick}
        dataTestId="preset-dropdown-text"
      />
    </GroupBy>
  );
};

export default PresetDropdown;
